'use client';

import React from 'react';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Container } from '@/components/ui/Container';
import { Heading } from '@/components/ui/Heading';
import { Button } from '@/components/ui/Button';
import { IndustriesCarousel } from './IndustriesCarousel';
import styles from './VenuesSlider.module.css';
import { type Locale } from '@/lib/i18n';

export interface VenueItem {
  name: string;
  image: string;
  url: string;
  altText: string;
}

interface VenuesSliderProps {
  label?: string;
  title: string;
  subtitle?: string;
  context?: string;
  venues: VenueItem[];
  ctaText?: string;
  ctaUrl?: string;
  locale: Locale;
  background?: 'primary' | 'secondary';
}

/**
 * VenuesSlider Component - UPGRADED with IndustriesCarousel
 * Full venues section with header, smooth infinite-loop carousel, and optional CTA
 * Used on homepage AND solution pages (kiosk, qr, tablet, webshop)
 * 
 * Features:
 * - Beautiful header with label + title + subtitle (SEO H2)
 * - Smooth infinite loop scrolling (no jumps or blank spaces)
 * - Pause on hover
 * - Lazy loading images with optimized sizes
 * - SEO-optimized internal links with proper alt texts
 * - Performance optimized with requestAnimationFrame
 * - Optional CTA button
 */
export function VenuesSlider({ 
  label,
  title, 
  subtitle,
  context,
  venues, 
  ctaText, 
  ctaUrl,
  locale,
  background = 'secondary'
}: VenuesSliderProps) {
  
  // Transform venues to industries format
  const industries = venues.map(venue => ({
    key: venue.name.toLowerCase().replace(/\s+/g, '-'),
    name: venue.name,
    image: venue.image,
    href: venue.url,
    alt: venue.altText,
  }));
  
  return (
    <Section background={background} className={styles.venuesSection}>
      <Container>
        {/* Header - SEO optimized with label + H2 + subtitle */}
        <div className={styles.sectionHeader}>
          {label && <span className={styles.sectionLabel}>{label}</span>}
          <Heading as="h2" className={styles.sectionTitle}>
            {title}
          </Heading>
          {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
          {context && <p className={styles.sectionContext}>{context}</p>}
        </div>
        
        {/* Upgraded carousel with smooth infinite loop */}
        <IndustriesCarousel 
          industries={industries}
          variant="double-row"
          autoPlaySpeed={60}
        />

        {/* Optional CTA */}
        {ctaText && ctaUrl && (
          <div className={styles.venuesCta}>
            <Link href={ctaUrl}>
              <Button variant="primary" size="lg">
                {ctaText}
              </Button>
            </Link>
          </div>
        )}
      </Container>
    </Section>
  );
}
